import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { firstValueFrom } from "rxjs";
import { GetReservationsResponse } from "../models/http/SearchReservationsResponse";
import { Reservation } from "../models/Reservation";
import { GetLegacyReservationsResponse } from "../models/http/GetLegacyReservationsResponse";
import { ReservationHistory } from "../models/ReservationHistory";
import { UserContextProvider } from "../../../infrastructure/context/user-context-provider";
import { PortalBeHttpClient } from "../../../infrastructure/http/legacy/PortalBeHttpClient";
import { AdminPortalAPIHttpClient } from "../../../infrastructure/http/v2/AdminPortalAPIHttpClient";
import { SearchParams } from "../../../shared/search/SearchParams";
import { isStringEmptyOrNull } from "../../../utilities/StringUtility";
import { SaveReservationImportDataResponse } from "../models/http/SaveReservationImportDataResponse";

export interface GetReservationsParams {
  page: number;
  pageSize: number;
  upcoming?: boolean;
  isHome?: boolean;
  completed?: boolean;
  canceled?: boolean;
  claimFiled?: boolean;
  arrivalDate?: string;
  departureDate?: string;
  organizationId?: number;
  guestId?: number;
}

@Injectable({ providedIn: 'root' })
export class ReservationService {

  lastSearchRequest?: SearchParams;
  lastSearchResponse?: GetReservationsResponse;

  constructor(
    private adminPortalApiHttpClient: AdminPortalAPIHttpClient,
    private portalBeHttpClient: PortalBeHttpClient,
    private snackbar: MatSnackBar,
    private userContextProvider: UserContextProvider
  ) { }

  getReservationById = async (reservationId: any): Promise<Reservation> => {
    try {
      if (!reservationId) {
        return Promise.reject(`Reservation ID is null`);
      }

      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/reservations/${reservationId}`));
      if (!response || response.err) {
        return Promise.reject(`Get reservation response null ${reservationId}`);
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving reservation ${reservationId} ${e}`);
    }
  }

  getReservationByPropertyId = async (propertyId: number): Promise<any> => {
    try {
      if (!propertyId) {
        return Promise.reject(`Property ID is null`);
      }

      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/reservations/property/${propertyId}`));
      if (!response || response.err) {
        return Promise.reject(`Get reservations by propertyId response null: ${propertyId}`);
      }

      return Promise.resolve(response);
    } catch (e) {
      return Promise.reject(`Error while retrieving reservations by propertyId: ${propertyId}, error: ${e}`);
    }
  }

  getLegacyReservations = async (params: GetReservationsParams): Promise<GetLegacyReservationsResponse> => {
    const optionalParams: any = {};
    if (params.arrivalDate) {
      optionalParams['from_date'] = params.arrivalDate;
    }

    if (params.departureDate) {
      optionalParams['to_date'] = params.departureDate;
    }

    if(params.guestId) {
      optionalParams['guest_id'] = params.guestId;
    }

    if(params.organizationId) {
      optionalParams['organization_id'] = params.organizationId;
    }

    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get('/reservations', {
        params:
        {
          limit: params.pageSize,
          p: params.page,
          upcoming: params.upcoming ? 0 : 1,
          in_home: params.isHome ? 0 : 1,
          completed: params.completed ? 0 : 1,
          canceled: params.canceled ? 0 : 1,
          claim_filed: params.claimFiled ? 0 : 1,
          ...optionalParams
        }
      }));

      if (!response) {
        return Promise.reject(`Get reservations response null`);
      }

      return Promise.resolve({
        reservations: response.data,
        totalRecords: response.total
      });
    } catch (e) {
      return Promise.reject(`Error while retrieving reservations ${e}`);
    }
  }

  async searchReservations(searchParams: SearchParams): Promise<GetReservationsResponse> {
    try {
      this.lastSearchRequest = searchParams;

      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post('/reservations/search', {...searchParams}));
      if (!response) {
        return Promise.reject(`Search reservations response null`);
      }

      return Promise.resolve({
        reservations: response.data,
        totalRecords: response.metaData.totalRecords
      });
    } catch (e) {
      return Promise.reject(`Error while searching reservations: ${e}`);
    }
  }

  async searchReservationsByText(keyword: string) {
    let params: SearchParams = { page:1, pageSize: 10 };
    params.freeText = keyword;
    const response = await this.searchReservations(params);
    return response; 
  }

  getReservationHistory = async (reservationId: number): Promise<ReservationHistory[]> => {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/reservations/${reservationId}/history`));
      if (!response) {
        return Promise.reject(`Get reservation history response null ${reservationId}`);
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving reservation history ${reservationId} ${JSON.stringify(e)}`);
    }
  }

  saveReservationImportData = async (fullImportDataPayload: any): Promise<SaveReservationImportDataResponse> => {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post(`/reservations/saveImportData`, fullImportDataPayload ));

      const result: SaveReservationImportDataResponse = response;
      if (!result) {
        return Promise.reject(`Import Reservation Data response null`);
      }

      const failedReservationsLength = result.failedReservationsLength ?? result.failedReservations?.length ?? 0;
      const createdReservationsLength = result.createdReservationsLength ?? result.createdReservations?.length ?? 0;

      if (failedReservationsLength) {
        const failureSummary = result.failedReservations.map(failedRes => `Booking ${failedRes.reservationReferenceId}: ${failedRes.message}`).join('; ');
        this.displayErrorSnackbar(`${failedReservationsLength} reservation(s) failed to import: ${failureSummary}`);
      } else {
        this.displayImportSuccessSnackbar(createdReservationsLength);
      }

      return Promise.resolve(result);
    } catch (e) {
      if (e) {
        this.displayErrorSnackbar(`Error while saving reservation import data: ${JSON.stringify(e)}`);
      }
      return Promise.reject(`Error while saving reservation import data: ${JSON.stringify(e)}`);
    }
  }

  getReservationCoverageReport = (reservationId: number, reportType: number) => {
    const token = this.userContextProvider.account?.token;
    const url = `${this.portalBeHttpClient.getBaseApiUrl()}/reservations/export-coverage?reservation_id=${reservationId}&report_type=${reportType}&bearer_token=${token}`;
    window.open(url, '_blank');
  }

  async createReservation(reservation: Reservation): Promise<Reservation> {
    try {
      const payload: Reservation = {
        id: 0,
        reservation_id: 0,
        reservation_reference_id: reservation.reservation_reference_id,
        reference_id: reservation.reference_id,
        booking_date: reservation.booking_date,
        arrival_date: reservation.arrival_date,
        departure_date: reservation.departure_date,
        insurance_cost: reservation.insurance_cost,
        insurance_refund_amount: 0,
        insurance_refunded: false,
        number_of_guests: reservation.number_of_guests,
        number_of_nights: reservation.number_of_nights,
        reservation_cost: reservation.reservation_cost,
        reservation_type: reservation.reservation_type,
        status: reservation.status,
        reservation_currency_id: reservation.reservation_currency_id,
        updated_insurance_cost: reservation.insurance_cost,
        exclude_from_invoice: false,
        verification_cost: reservation.verification_cost,
        updated_verification_cost: reservation.verification_cost,
        verification_paid: false,
        status_id: reservation.status_id,
        status_history: reservation.status_history,
        claim_token: '',
        claim_count: 0,
        score: 0,
        rating: '0',
        property_id: reservation.property_id,
        property: reservation.property,
        organization_id: reservation.organization_id.toString(),
        reservation_currency: reservation.reservation_currency,
        for_guest_verification: reservation.for_guest_verification,
        for_reservation: 1,
        selected_policies: reservation.selected_policies,
        reservation_guests: reservation.reservation_guests,
        guests: reservation.guests
      }

      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post(`/reservations`, payload));
      if (response.err === null) {
        this.displaySuccessSnackbar();
      } else {
        this.displayErrorSnackbar(response.err.msg);
      }

      return response.data;
    } catch (e) {
      return Promise.reject(`Error while creating reservation ${JSON.stringify(e)}`);
    }
  }

  async updateReservation(reservationId: number, reservation: any): Promise<Reservation> {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.put(`/reservations/${reservationId}`, reservation));
      if (!response) {
        return Promise.reject(`Update reservation response null ${reservationId}`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while updating reservation ${reservationId} ${e}`);
    }
  }

  async updateReservationStatus(reservation: Reservation, statusPayload: any): Promise<Reservation[]> {
    const reservationId = reservation.reservation_id;
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.put(`/reservations/${reservationId}/update-status`, statusPayload));
      if (!response) {
        return Promise.reject(`Update reservation response null ${reservationId}`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displayStatusSuccessSnackbar(reservationId);
        }
      }

      return Promise.resolve(response.data);
    } catch (e: any) {
      return Promise.reject(`Error while updating reservation status: ${e.message}`);
    }
  }

  async updateReservationInfo(reservation: Reservation, statusPayload: any): Promise<Reservation[]> {
    const reservationId = reservation.reservation_id;
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.put(`/reservations/${reservationId}/update-info`, statusPayload));
      this.displaySnackbar(response.err);
      return Promise.resolve(response.data);
    } catch (e: any) {
      return Promise.reject(`Error while updating reservation information: ${e.message}`);
    }
  }

  async cancelReservation(reservationId: number | string, cancelDate: Date): Promise<any> {
    try {
      const response = await firstValueFrom(this.adminPortalApiHttpClient.put(`/reservations/${reservationId}/update-status`, { 
        id: reservationId,
        status_date: cancelDate,
        status_id: 3
      }));

      if (!response) {
        return Promise.reject(`Cancel reservation response null ${reservationId}`);
      }

      return Promise.resolve(response);
    } catch (e) {
      return Promise.reject(`Error while canceling reservation ${reservationId} ${JSON.stringify(e)}`);
    }
  }

  async updateReservationInvoiceStatusAfterCancelation(reservationId: number | string): Promise<any> {
    try {
      const response = await firstValueFrom(this.adminPortalApiHttpClient.put(`/reservations/${reservationId}/update-info`, {
        exclude_from_invoice: 1,
        id: reservationId,
        verification_paid: 1
      }));

      if (!response) {
        return Promise.reject(`Update reservation invoice status after cancelation response null ${reservationId}`);
      }

      return Promise.resolve(response);
    } catch (e) {
      return Promise.reject(`Error while updating reservation invoice status after cancelation ${reservationId} ${JSON.stringify(e)}`);
    }
  }

  displaySnackbar(errorMessage: string) {
    if (isStringEmptyOrNull(errorMessage)) {
      this.displaySuccessSnackbar();
    } else {
      this.displayErrorSnackbar(errorMessage);
    }
  }
    
  displaySuccessSnackbar() {
    this.snackbar.open('Save Successful', 'OK', { duration: 4000 });
  }

  displayImportSuccessSnackbar(reservationCount: number) {
    this.snackbar.open(`Save Successful - ${reservationCount} Reservation${reservationCount === 1 ? '' : 's'} Created`, 'OK', { duration: 4000 });
  }

  displayErrorSnackbar(errorMessage: string) {
    this.snackbar.open(`Error Saving Reservation: ${errorMessage}`, 'OK', { duration: 4000 });
  }

  displayStatusSuccessSnackbar(reservationId: any) {
    this.snackbar.open(`Reservation #${reservationId} - Status Update Successful`, 'OK', { duration: 4000 });
  }
}